// ============================================================================
// BP-09-04: Support Groups Hooks
// BrightPath Community - Support Groups React Query Hooks
// ============================================================================

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { supportGroupApi } from '../api';
import {
  SupportGroup,
  SupportGroupDetailResponse,
  SupportGroupMember,
  SupportGroupMeeting,
  SupportGroupSearchParams,
  CreateSupportGroupInput,
  UpdateSupportGroupInput,
  CreateMeetingInput,
  UpdateMemberInput,
} from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const supportGroupKeys = {
  all: ['support-groups'] as const,
  list: (params?: SupportGroupSearchParams) => [...supportGroupKeys.all, 'list', params] as const,
  detail: (id: string) => [...supportGroupKeys.all, 'detail', id] as const,
  stats: () => [...supportGroupKeys.all, 'stats'] as const,
  myGroups: () => [...supportGroupKeys.all, 'my-groups'] as const,
  members: (groupId: string) => [...supportGroupKeys.all, 'members', groupId] as const,
  meetings: (groupId: string) => [...supportGroupKeys.all, 'meetings', groupId] as const,
  upcomingMeetings: () => [...supportGroupKeys.all, 'upcoming-meetings'] as const,
};

// =============================================================================
// Group List Hooks
// =============================================================================

export function useSupportGroups(params?: SupportGroupSearchParams) {
  return useQuery({
    queryKey: supportGroupKeys.list(params),
    queryFn: () => supportGroupApi.getGroups(params),
  });
}

export function useInfiniteSupportGroups(params?: Omit<SupportGroupSearchParams, 'page'>) {
  return useInfiniteQuery({
    queryKey: supportGroupKeys.list(params),
    queryFn: ({ pageParam = 1 }) => supportGroupApi.getGroups({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.total_pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
  });
}

export function useSupportGroup(
  groupId: string,
  options?: Omit<UseQueryOptions<SupportGroupDetailResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: supportGroupKeys.detail(groupId),
    queryFn: () => supportGroupApi.getGroup(groupId),
    enabled: !!groupId,
    ...options,
  });
}

export function useSupportGroupStats() {
  return useQuery({
    queryKey: supportGroupKeys.stats(),
    queryFn: () => supportGroupApi.getGroupStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useMyGroups() {
  return useQuery({
    queryKey: supportGroupKeys.myGroups(),
    queryFn: () => supportGroupApi.getMyGroups(),
  });
}

// =============================================================================
// Group CRUD Mutations
// =============================================================================

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSupportGroupInput) => supportGroupApi.createGroup(data),
    onSuccess: (newGroup) => {
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.list() });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.myGroups() });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.stats() });
    },
  });
}

export function useUpdateGroup(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateSupportGroupInput) => supportGroupApi.updateGroup(groupId, data),
    onSuccess: (updatedGroup) => {
      queryClient.setQueryData(supportGroupKeys.detail(groupId), (old: SupportGroupDetailResponse) => ({
        ...old,
        group: updatedGroup,
      }));
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.list() });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.myGroups() });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => supportGroupApi.deleteGroup(groupId),
    onSuccess: (_, groupId) => {
      queryClient.removeQueries({ queryKey: supportGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.list() });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.myGroups() });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.stats() });
    },
  });
}

// =============================================================================
// Membership Hooks
// =============================================================================

export function useJoinGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => supportGroupApi.joinGroup(groupId),
    onSuccess: (_, groupId) => {
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.myGroups() });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.list() });
    },
  });
}

export function useLeaveGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => supportGroupApi.leaveGroup(groupId),
    onSuccess: (_, groupId) => {
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.myGroups() });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.list() });
    },
  });
}

export function useGroupMembers(groupId: string) {
  return useQuery({
    queryKey: supportGroupKeys.members(groupId),
    queryFn: () => supportGroupApi.getGroupMembers(groupId),
    enabled: !!groupId,
  });
}

export function useUpdateMember(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateMemberInput }) =>
      supportGroupApi.updateMember(groupId, userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.members(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.detail(groupId) });
    },
  });
}

export function useRemoveMember(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => supportGroupApi.removeMember(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.members(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.list() });
    },
  });
}

export function useApproveMember(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => supportGroupApi.approveMember(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.members(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.detail(groupId) });
    },
  });
}

// =============================================================================
// Meeting Hooks
// =============================================================================

export function useGroupMeetings(groupId: string) {
  return useQuery({
    queryKey: supportGroupKeys.meetings(groupId),
    queryFn: () => supportGroupApi.getGroupMeetings(groupId),
    enabled: !!groupId,
  });
}

export function useUpcomingMeetings() {
  return useQuery({
    queryKey: supportGroupKeys.upcomingMeetings(),
    queryFn: () => supportGroupApi.getUpcomingMeetings(),
  });
}

export function useCreateMeeting(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMeetingInput) => supportGroupApi.createMeeting(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.meetings(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.upcomingMeetings() });
    },
  });
}

export function useUpdateMeeting(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ meetingId, data }: { meetingId: string; data: Partial<CreateMeetingInput> }) =>
      supportGroupApi.updateMeeting(meetingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.meetings(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.upcomingMeetings() });
    },
  });
}

export function useDeleteMeeting(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (meetingId: string) => supportGroupApi.deleteMeeting(meetingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.meetings(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.upcomingMeetings() });
    },
  });
}

export function useRecordAttendance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (meetingId: string) => supportGroupApi.recordAttendance(meetingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportGroupKeys.upcomingMeetings() });
    },
  });
}
